import type { QueryClient, QueryObserverOptions } from '@tanstack/react-query';
import type { Universe } from '@thinkalpha/platform-ws-client/contracts/universe.js';
import { getUniverses, getUniverseById } from 'src/lib/universes';

// Constants
export const UNIVERSES = 'universes';
export const ALL = 'all';
export const DEFAULT = 'default';

/**
 * Get a universe by ID.
 */
export function getUniverseByIdQuery(id: string): QueryObserverOptions<Universe | undefined> {
    return {
        queryKey: [UNIVERSES, id],
        queryFn: async () => await getUniverseById(id),
    };
}

export function hydrateUniverseByIdQuery(queryClient: QueryClient, universe: Universe): void {
    queryClient.setQueryData(getUniverseByIdQuery(universe.id).queryKey, universe);
    if (universe.default) {
        queryClient.setQueryData(getDefaultUniverseRecordQuery().queryKey, universe);
    }
}

/**
 * Get universes for a user.
 */
export function getUniversesQuery(): QueryObserverOptions<Universe[]> {
    return {
        queryKey: [UNIVERSES, ALL],
        queryFn: () => getUniverses(),
    };
}

/**
 * The last cached universes list, without fetching. Keeps the query key
 * private to this module so cache reads cannot drift from the query shape.
 */
export function getUniversesQueryData(queryClient: QueryClient): Universe[] | undefined {
    return queryClient.getQueryData<Universe[]>([UNIVERSES, ALL]);
}

/**
 * Gets the default universe from the list of universes
 */
export function getDefaultUniverseRecordQuery(): QueryObserverOptions<Universe> {
    return {
        queryKey: [UNIVERSES, DEFAULT],
        queryFn: async () => {
            const universe = (await getUniverses()).find((candidate) => candidate.default);
            if (!universe) throw new Error('No default universe is configured');
            return universe;
        },
    };
}
